import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const iconButtonSizes = ['xs', 'sm', 'base', 'lg'] as const;
export type IconButtonSize = (typeof iconButtonSizes)[number];

export const iconButtonShapes = ['square', 'circle'] as const;
export type IconButtonShape = (typeof iconButtonShapes)[number];

export const iconButtonTones = [
  'subtle',
  'secondary',
  'tertiary',
  'quaternary',
  'error',
  /** White-on-translucent — for sitting inside the colored header chip. */
  'inverse',
] as const;
export type IconButtonTone = (typeof iconButtonTones)[number];

interface SizeSpec {
  box: string;
  icon: number;
}

const sizeSpecs: Record<IconButtonSize, SizeSpec> = {
  xs: { box: 'w-6 h-6', icon: 10 },
  sm: { box: 'w-7 h-7', icon: 12 },
  base: { box: 'w-9 h-9', icon: 14 },
  lg: { box: 'w-11 h-11', icon: 18 },
};

const toneClasses: Record<IconButtonTone, string> = {
  subtle:
    'text-content-subtle hover:text-content-text hover:bg-content-focal disabled:hover:bg-transparent disabled:opacity-40',
  secondary:
    'text-content-subtle hover:text-secondary-emphasized hover:bg-secondary-subtle disabled:opacity-40',
  tertiary:
    'text-content-subtle hover:text-tertiary-emphasized hover:bg-tertiary-subtle disabled:opacity-40',
  quaternary:
    'text-content-subtle hover:text-quaternary-emphasized hover:bg-quaternary-subtle disabled:opacity-40',
  error: 'text-content-disabled hover:text-error-main hover:bg-error-badge disabled:opacity-40',
  inverse: 'text-white/80 hover:text-white hover:bg-white/15 disabled:opacity-40',
};

/** Duotone-icon color override. Maps to the FA `--fa-primary-color` /
 *  `--fa-secondary-color` CSS variables. Used for icons rendered against
 *  colored backgrounds where the default `currentColor` treatment doesn't
 *  read well — e.g. the white-on-purple header chip. */
export const iconButtonDuotoneVariants = [
  /** Default — icon inherits color from the button's `tone`. */
  'auto',
  /** White-on-translucent: both duotone layers go white. Used inside the
   *  inverse-tone header chip. */
  'inverse',
] as const;
export type IconButtonDuotoneVariant = (typeof iconButtonDuotoneVariants)[number];

type FAIconCssVars = React.CSSProperties & { [key: `--fa-${string}`]: string };

const duotoneStyles: Record<IconButtonDuotoneVariant, FAIconCssVars> = {
  auto: {},
  inverse: {
    '--fa-primary-color': 'white',
    '--fa-secondary-color': 'rgba(255,255,255,0.85)',
  } as FAIconCssVars,
};

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  icon: IconDefinition;
  /** Accessible label — required since the button has no text. */
  ariaLabel: string;
  size?: IconButtonSize;
  shape?: IconButtonShape;
  tone?: IconButtonTone;
  /** Force the "active" / "selected" visual treatment (matches hover). */
  active?: boolean;
  /** Override the icon's pixel size (e.g. for icons that read smaller). */
  iconSize?: number;
  /** Duotone color override for FA pro-duotone icons. Defaults to `auto`
   *  (icon inherits the tone's color). Use `inverse` for white-on-color
   *  contexts like the header chip. */
  duotoneVariant?: IconButtonDuotoneVariant;
  className?: ClassName;
}

/**
 * Generic icon-only button. Used across the app for row actions (note,
 * schedule, remove), header chip controls (tutorial, refresh, view-trip),
 * and toolbar buttons.
 *
 * The `tone` controls hover/active colors so the same shape can read as a
 * neutral, semantic-positive, or semantic-destructive action.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      ariaLabel,
      size = 'sm',
      shape = 'square',
      tone = 'subtle',
      active = false,
      iconSize,
      duotoneVariant = 'auto',
      className,
      style,
      type,
      ...rest
    },
    ref,
  ) => {
    const spec = sizeSpecs[size];
    const px = iconSize ?? spec.icon;
    // Duotone CSS vars cascade from the button down to the FontAwesome
    // SVG via CSS inheritance — the icon child reads them automatically.
    const buttonStyle =
      duotoneVariant === 'auto' ? style : { ...duotoneStyles[duotoneVariant], ...style };
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        aria-label={ariaLabel}
        title={rest.title ?? ariaLabel}
        style={buttonStyle}
        className={twx(
          'shrink-0 flex items-center justify-center transition-[colors,transform] duration-150 cursor-pointer disabled:cursor-not-allowed motion-safe:active:scale-[0.9]',
          spec.box,
          shape === 'circle' ? 'rounded-full' : 'rounded-lg',
          toneClasses[tone],
          // Active states echo the hover treatment so the button signals "on".
          active &&
            (tone === 'secondary'
              ? 'text-secondary-emphasized bg-secondary-subtle'
              : tone === 'tertiary'
                ? 'text-tertiary-emphasized bg-tertiary-subtle'
                : tone === 'quaternary'
                  ? 'text-quaternary-emphasized bg-quaternary-subtle'
                  : 'text-content-text bg-content-focal'),
          className,
        )}
        {...rest}
      >
        <FontAwesomeIcon icon={icon} style={{ width: px, height: px }} />
      </button>
    );
  },
);
IconButton.displayName = 'IconButton';
